import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { isApiError } from "@bw/shared/api";

import { correctEligibility, type EligibilityCorrection } from "../api";

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
] as const;

const MIN_REASON = 10;

/** Today as YYYY-MM-DD in the admin's own time zone. */
function localToday(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Players can't change their date of birth or gender after sign-up; support
 * corrects them here with a recorded reason. A corrected date of birth
 * re-derives the parental-consent gate on the server.
 */
export function UserEligibilityCorrectionPanel({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState<EligibilityCorrection["gender"] | "">(
    "",
  );
  const [reason, setReason] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      correctEligibility(userId, {
        ...(dateOfBirth ? { date_of_birth: dateOfBirth } : {}),
        ...(gender ? { gender } : {}),
        reason: reason.trim(),
      }),
    onSuccess: async (profile) => {
      const parts = [
        dateOfBirth ? "date of birth" : null,
        gender ? "gender" : null,
      ].filter(Boolean);
      const gated = profile.account_status === "pending_parental_consent";
      setSaved(
        `Corrected ${parts.join(" and ")}.${gated ? " The account now needs a guardian's consent." : ""}`,
      );
      setDateOfBirth("");
      setGender("");
      setReason("");
      setOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["platform", "user", userId],
      });
    },
  });

  const today = localToday();
  const futureDob = Boolean(dateOfBirth) && dateOfBirth > today;
  const hasChange = Boolean(dateOfBirth || gender);
  const reasonOk = reason.trim().length >= MIN_REASON;

  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
        Eligibility correction
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Date of birth and gender are locked for the player. Correct them only on
        a verified request.
      </Typography>
      {saved ? (
        <Alert severity="success" role="status" sx={{ mt: 2 }}>
          {saved}
        </Alert>
      ) : null}
      {open ? (
        <Stack
          component="form"
          spacing={2}
          sx={{ mt: 2 }}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (hasChange && reasonOk && !futureDob) {
              setSaved(null);
              mutation.mutate();
            }
          }}
        >
          <TextField
            label="Corrected date of birth"
            type="date"
            size="small"
            value={dateOfBirth}
            onChange={(event) => setDateOfBirth(event.target.value)}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { max: today },
            }}
            error={futureDob}
            helperText={
              futureDob
                ? "A date of birth can't be in the future."
                : "Leave empty to keep the current value."
            }
          />
          <TextField
            select
            label="Corrected gender"
            size="small"
            value={gender ?? ""}
            onChange={(event) =>
              setGender(
                event.target.value as EligibilityCorrection["gender"] | "",
              )
            }
          >
            <MenuItem value="">No change</MenuItem>
            {GENDER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Correction note"
            required
            multiline
            minRows={2}
            size="small"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            helperText={`Why it is needed. Recorded in the audit log; at least ${MIN_REASON} characters.`}
          />
          {mutation.isError ? (
            <Alert severity="error" role="alert">
              The correction wasn&apos;t saved.{" "}
              {isApiError(mutation.error) && mutation.error.message
                ? mutation.error.message
                : "Try again."}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !hasChange || !reasonOk || futureDob || mutation.isPending
              }
            >
              Save correction
            </Button>
            <Button
              type="button"
              onClick={() => {
                setOpen(false);
                setDateOfBirth("");
                setGender("");
                setReason("");
                mutation.reset();
              }}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Button
          type="button"
          variant="outlined"
          onClick={() => {
            setSaved(null);
            mutation.reset();
            setOpen(true);
          }}
          sx={{ mt: 2 }}
        >
          Correct date of birth or gender
        </Button>
      )}
    </Paper>
  );
}
